import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface AdminButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

const AdminButton: React.FC<AdminButtonProps> = ({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = ''
}) => {
  const { colors } = useTheme();

  const sizeClasses = {
    sm: 'py-1 px-3 text-sm',
    md: 'py-2 px-4',
    lg: 'py-3 px-6 text-lg'
  };

  const baseClasses = `font-medium rounded-lg transition-all duration-300 hover:scale-105 ${sizeClasses[size]} ${className}`;

  const getButtonStyle = () => {
    if (variant === 'danger') {
      return {
        backgroundColor: disabled ? colors.error + '60' : colors.error,
        color: 'white',
        opacity: disabled ? 0.6 : 1
      };
    }

    if (variant === 'secondary') {
      return {
        backgroundColor: 'transparent',
        color: colors.accent.mint,
        border: `2px solid ${colors.accent.mint}`,
        opacity: disabled ? 0.6 : 1
      };
    }

    // Primary variant
    return {
      background: disabled ? colors.text.secondary : `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
      color: 'white',
      opacity: disabled ? 0.6 : 1
    };
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    
    if (variant === 'primary') {
      e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
    } else if (variant === 'secondary') {
      e.currentTarget.style.backgroundColor = `${colors.accent.mint}20`;
    } else if (variant === 'danger') {
      e.currentTarget.style.backgroundColor = colors.error + 'dd';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    
    const style = getButtonStyle();
    Object.assign(e.currentTarget.style, style);
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={baseClasses}
      style={getButtonStyle()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </button>
  );
};

export default AdminButton;